import { startTransition, useCallback, useEffect, useRef, useState } from 'react';

// How many rows are mounted at any time, and how many of them sit above the
// viewport top. The window therefore extends well below the viewport too, so
// ordinary scrolling in either direction hits already-mounted rows before the
// window has to move.
const WINDOW_SIZE = 120;
const OVERSCAN = 25;

// The window start only ever lands on multiples of this, so a continuous
// scroll re-renders once per QUANTUM rows rather than at every row boundary.
// Coverage still holds at the extremes: rows above the viewport top range
// [OVERSCAN, OVERSCAN + QUANTUM - 1], leaving at least
// WINDOW_SIZE - OVERSCAN - QUANTUM + 1 = 71 rows below it — more than even a
// full-height 4K viewport shows.
const QUANTUM = 25;

// Windowed ("virtual") scrolling over a fully in-memory row array. Only
// WINDOW_SIZE rows are mounted; two spacer elements — one above the window,
// one below — are stretched to the pixel height of the unmounted rows so the
// scrollable content keeps the height it would have if every row were
// rendered. The scrollbar consequently reflects the whole dataset, and native
// scrolling (momentum, thumb drags, PageDown) needs no interception.
//
// The table's host container is NOT assumed to be the thing that scrolls:
// depending on the surrounding layout it may be unconstrained and grow, with
// some ancestor (a pane, or the page itself) doing the actual scrolling. The
// window position is therefore derived from geometry, not scrollTop: the top
// spacer occupies exactly start × rowHeight px, so the tbody's top edge is
// where row 0 would start, and
//
//   rows scrolled past = (visible top − tbody top) / rowHeight
//
// holds no matter which ancestor moved the content. Scroll events don't
// bubble, so listeners are bound directly on the container, every ancestor
// element, and window when the container attaches — whichever of them is the
// real scroller, we hear it — coalesced to at most one recompute per
// animation frame. `visible top` is approximated as max(0, container top) —
// exact when the container itself clips (the common case); when an ancestor
// clips below the viewport top, the error is that ancestor's own offset,
// absorbed by OVERSCAN.
//
// Row heights must be uniform (single-line, no-wrap cells). The exact height
// is measured off the first mounted row via `measureRow`, so the maths track
// the active theme; `defaultRowHeight` only covers the first paint.
//
// A component with two mutually exclusive scroll containers (inline view and
// an expanded dialog) attaches `attachContainer` to both: a mounting
// container that clips is scrolled back to the last known offset, and a
// recompute is scheduled either way, so the window aligns to wherever the
// active scroller actually is.
export default function useVirtualWindow(totalRows, { defaultRowHeight }) {
  const [start, setStart] = useState(0);
  const [rowHeight, setRowHeight] = useState(defaultRowHeight);
  const startRef = useRef(0);
  const rowHeightRef = useRef(defaultRowHeight);
  const rowsAbovePxRef = useRef(0);
  const containerRef = useRef(null);
  const rafRef = useRef(null);

  const recompute = useCallback(() => {
    const node = containerRef.current;
    if (!node) return;
    const tbody = node.querySelector('tbody');
    if (!tbody) return;
    const visibleTop = Math.max(0, node.getBoundingClientRect().top);
    const rowsAbovePx = visibleTop - tbody.getBoundingClientRect().top;
    rowsAbovePxRef.current = Math.max(0, rowsAbovePx);
    const raw = Math.max(
      0,
      Math.floor(rowsAbovePx / rowHeightRef.current) - OVERSCAN,
    );
    const next = Math.floor(raw / QUANTUM) * QUANTUM;
    if (next !== startRef.current) {
      startRef.current = next;
      // Rendered as a transition: React builds the new window in short slices,
      // letting the browser paint and handle input in between, where an ordinary
      // update renders a wide window in one block the browser cannot interrupt.
      // A window render already under way still finishes, but every move requested
      // meanwhile is folded into the next one, which goes straight to the latest
      // position. During a fast drag, rows can show blank until it lands.
      startTransition(() => {
        setStart(next);
      });
    }
  }, []);

  // At most one recompute per animation frame, no matter how many scroll
  // events the browser delivers.
  const scheduleRecompute = useCallback(() => {
    if (rafRef.current != null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      recompute();
    });
  }, [recompute]);

  useEffect(() => {
    window.addEventListener('resize', scheduleRecompute);
    return () => {
      window.removeEventListener('resize', scheduleRecompute);
      // Clear the ref when cancelling: the callback is the only other place
      // that resets it, and a cancelled callback never runs. Leaving the ref
      // set wedges the schedule gate shut permanently — StrictMode's
      // mount/cleanup/remount hits this on every page load, because
      // attachContainer schedules a recompute at mount.
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [scheduleRecompute]);

  // Scroll listeners live on the container, every ancestor element, and
  // window: listeners on non-scrolling elements simply never fire, and the
  // one element that does scroll — whichever it is — is guaranteed to be in
  // that set. Rebound whenever a container attaches (inline view vs dialog),
  // unbound when it detaches.
  const boundTargetsRef = useRef([]);
  const bindScrollTargets = useCallback((node) => {
    boundTargetsRef.current.forEach((target) => {
      target.removeEventListener('scroll', scheduleRecompute);
    });
    boundTargetsRef.current = [];
    if (!node) return;
    const targets = [];
    let el = node;
    while (el) {
      targets.push(el);
      el = el.parentElement;
    }
    targets.push(window);
    targets.forEach((target) => {
      target.addEventListener('scroll', scheduleRecompute, { passive: true });
    });
    boundTargetsRef.current = targets;
  }, [scheduleRecompute]);

  const attachContainer = useCallback((node) => {
    containerRef.current = node;
    bindScrollTargets(node);
    if (node) {
      // Restores the reading position where the node itself is the clipping
      // scroller (the expanded dialog, the data page); a harmless no-op where
      // an ancestor scrolls instead. The recompute then aligns the window to
      // wherever the active scroller actually sits.
      node.scrollTop = rowsAbovePxRef.current;
      scheduleRecompute();
    }
  }, [bindScrollTargets, scheduleRecompute]);

  // Callback ref for the first mounted data row. Re-runs whenever that row's
  // element identity changes (the window moved), so the guard keeps it from
  // ever looping on a stable height.
  const measureRow = useCallback((node) => {
    if (!node) return;
    const measured = node.getBoundingClientRect().height;
    if (measured > 0 && Math.abs(measured - rowHeightRef.current) > 0.5) {
      rowHeightRef.current = measured;
      setRowHeight(measured);
      scheduleRecompute();
    }
  }, [scheduleRecompute]);

  // For when the underlying rows change (new fetch, re-sort): scroll a
  // clipping container back to the top, then realign the window with the
  // active scroller (an ancestor scroller keeps its own position).
  const resetWindow = useCallback(() => {
    rowsAbovePxRef.current = 0;
    startRef.current = 0;
    setStart(0);
    if (containerRef.current) containerRef.current.scrollTop = 0;
    scheduleRecompute();
  }, [scheduleRecompute]);

  // Clamp against the current row count in render rather than in the
  // recompute, so a dataset that shrinks underneath a deep scroll position
  // still yields a valid window on the very next render.
  const clampedStart = Math.max(0, Math.min(start, Math.max(0, totalRows - WINDOW_SIZE)));
  const end = Math.min(totalRows, clampedStart + WINDOW_SIZE);

  return {
    start: clampedStart,
    end,
    topPad: clampedStart * rowHeight,
    bottomPad: Math.max(0, (totalRows - end) * rowHeight),
    attachContainer,
    measureRow,
    resetWindow,
  };
}
